import axios from '@/assets/helpers/api'
import {
  onlyDigits,
  onlyAlphaNumeric,
  isFilled,
  validEmail,
  validFullName,
  validCpf,
  validRg,
  validBirthDate,
} from '@/composables/useFormValidation'

const STORAGE_KEY = 'weclix_internet_5g_checkout'

const stepOrder = [
  'availability',
  'contract',
  'documents',
  'revision',
  'conclusion'
]

const stepper = {
  availability: {
    routeName: 'Availability',
    label: 'Disponibilidade'
  },
  contract: {
    routeName: 'ContractData',
    label: 'Contrato'
  },
  documents: {
    routeName: 'Documents',
    label: 'Documentos'
  },
  revision: {
    routeName: 'Revision',
    label: 'Resumo'
  },
  conclusion: {
    routeName: 'Conclusion',
    label: 'Conclusao'
  }
}

const initialForm = () => ({
  availability: {
    name: '',
    email: '',
    phone: '',
    cep: '',
    city: '',
    neighborhood_id: '',
    neighborhood: '',
    address_id: '',
    address: '',
    address_number: '',
    address_complement: ''
  },
  contract: {
    cpf: '',
    rg: '',
    mother_name: '',
    birth_date: '',
    terms: false
  },
  documents: {
    address_proof_name: '',
    document_name: '',
    selfie_name: ''
  }
})

const initialCompleted = () => stepOrder.reduce((steps, step) => {
  steps[step] = false
  return steps
}, {})

const hasStorage = () => typeof window !== 'undefined' && window.localStorage

const persist = state => {
  if (!hasStorage()) return

  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    campaignParams: state.campaignParams,
    form: state.form,
    activeStep: state.activeStep,
    completed: state.completed
  }))
}

const getSessionUuid = state => {
  return state.campaignParams.session_uuid ||
    state.campaignParams.sessionUuid ||
    state.campaignParams.session ||
    (hasStorage() ? localStorage.getItem('session_uuid') ?? '' : '') ||
    ''
}

const getQuerySessionUuid = query => query?.session_uuid || query?.sessionUuid || query?.session || ''

const resetSessionIfSwitching = async ({ dispatch, state }, query) => {
  const queryHasSession = getQuerySessionUuid(query)
  const existingSession = getQuerySessionUuid(state.campaignParams)

  if (queryHasSession && existingSession && queryHasSession !== existingSession) {
    await dispatch('clearSession')
  }

  return queryHasSession
}

const localValidationErrors = form => {
  const errors = {
    availability: {},
    contract: {}
  }

  const availability = form.availability

  if (!validFullName(availability.name)) errors.availability.name = 'Informe nome e sobrenome'
  if (!validEmail(availability.email)) errors.availability.email = 'E-mail inválido'
  if (![10, 11].includes(onlyDigits(availability.phone).length)) errors.availability.phone = 'Número de telefone inválido'
  if (onlyDigits(availability.cep).length !== 8) errors.availability.cep = 'CEP deve conter 8 dígitos'
  if (!isFilled(availability.city)) errors.availability.city = 'Cidade é obrigatória'
  if (!isFilled(availability.neighborhood)) errors.availability.neighborhood = 'Bairro é obrigatório'
  if (!isFilled(availability.address)) errors.availability.address = 'Endereço é obrigatório'
  if (!isFilled(availability.address_number)) errors.availability.address_number = 'Número é obrigatório'

  const contract = form.contract

  if (!validCpf(contract.cpf)) errors.contract.cpf = 'CPF inválido'
  if (!validRg(contract.rg)) errors.contract.rg = 'RG deve conter 9 caracteres'
  if (!validFullName(contract.mother_name)) errors.contract.mother_name = 'Informe nome e sobrenome'
  if (!validBirthDate(contract.birth_date)) errors.contract.birth_date = 'Data de nascimento inválida'
  if (!contract.terms) errors.contract.terms = 'Aceite os termos para continuar'

  return errors
}

const isStepValid = (form, stepKey) => {
  const errors = localValidationErrors(form)

  if (stepKey === 'availability') {
    const data = form.availability
    const requiredFields = [
      data.name,
      data.email,
      data.phone,
      data.cep,
      data.city,
      data.neighborhood,
      data.address,
      data.address_number
    ]

    return requiredFields.every(isFilled) &&
      !Object.keys(errors.availability).length
  }

  if (stepKey === 'contract') {
    return !Object.keys(errors.contract).length
  }

  if (stepKey === 'documents') {
    const data = form.documents
    return isFilled(data.address_proof_name) &&
      isFilled(data.document_name) &&
      isFilled(data.selfie_name)
  }

  if (stepKey === 'revision') return true
  if (stepKey === 'conclusion') return true

  return false
}

export default {
  namespaced: true,

  state: {
    stepOrder,
    stepper,
    activeStep: 'availability',
    completed: initialCompleted(),
    campaignParams: {},
    form: initialForm(),
    backendErrors: {
      availability: {},
      contract: {}
    },
    addressLookup: {
      isCepLoading: false,
      isAddressLoading: false,
      cepError: '',
      neighborhoods: [],
      streets: []
    },
    pixRequest: {
      inFlight: false,
      result: null
    },
    chipService: {
      name: null,
      price: null,
      loading: false,
      error: false,
    },
    pixEnabled: true,
    pixEnabledReady: false,
    confirmRequest: {
      inFlight: false,
      error: null
    }
  },

  getters: {
    form: state => state.form,
    campaignParams: state => state.campaignParams,
    stepOrder: state => state.stepOrder,
    stepper: state => state.stepper,
    activeStep: state => state.activeStep,
    completed: state => state.completed,
    backendErrors: state => state.backendErrors,
    validationErrors: state => {
      const errors = localValidationErrors(state.form)

      return {
        availability: {
          ...errors.availability,
          ...state.backendErrors.availability
        },
        contract: {
          ...errors.contract,
          ...state.backendErrors.contract
        }
      }
    },
    addressLookup: state => state.addressLookup,
    chipService: state => state.chipService,
    pixEnabled: state => state.pixEnabled,
    pixEnabledReady: state => state.pixEnabledReady,
    confirmRequest: state => state.confirmRequest,
    sessionUuid: state => getSessionUuid(state),
    effectiveStepOrder: state => state.pixEnabled
      ? state.stepOrder
      : state.stepOrder.filter(step => step !== 'conclusion'),
    stepIndex: (state, getters) => stepKey => getters.effectiveStepOrder.indexOf(stepKey),
    getStepByRouteName: state => routeName => {
      return Object.entries(state.stepper)
        .find(([, step]) => step.routeName === routeName)?.[0] ?? null
    },
    isStepValid: state => stepKey => isStepValid(state.form, stepKey),
    canAccessStep: (state, getters) => stepKey => {
      const effectiveOrder = getters.effectiveStepOrder
      const index = effectiveOrder.indexOf(stepKey)
      if (index === -1) return false
      if (index === 0) return true

      return effectiveOrder
        .slice(0, index)
        .every(key => state.completed[key] || isStepValid(state.form, key))
    }
  },

  actions: {
    async initializeFromQuery({ dispatch, commit, state }, query) {
      await dispatch('hydrateDraft')
      const queryHasSession = await resetSessionIfSwitching({ dispatch, state }, query)
      if (queryHasSession) commit('setCampaignParams', query)
      persist(state)
    },

    async initialize5gSession({ dispatch, commit, state }, query) {
      await dispatch('hydrateDraft')
      const queryHasSession = await resetSessionIfSwitching({ dispatch, state }, query)
      if (queryHasSession) commit('setCampaignParams', query)

      const sessionUuid = getSessionUuid(state)
      if (!sessionUuid) return

      if (!state.campaignParams.session_uuid && !state.campaignParams.sessionUuid && !state.campaignParams.session) {
        commit('setCampaignParams', { session_uuid: sessionUuid })
      }

      persist(state)
      await dispatch('fetchSessionOnly', sessionUuid, { root: true })
    },

    hydrateDraft({ commit }) {
      if (!hasStorage()) return

      const rawDraft = localStorage.getItem(STORAGE_KEY)
      if (!rawDraft) return

      try {
        commit('hydrateDraft', JSON.parse(rawDraft))
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY)
      }
    },

    updateFormSection({ commit, state }, payload) {
      commit('updateFormSection', payload)
      persist(state)
    },

    async fetchAddressDataByCep({ commit, state }, cep) {
      const sessionUuid = getSessionUuid(state)
      const cleanCep = onlyDigits(cep)

      commit('setAddressLookupError', '')
      commit('setAddressLookupLists', { neighborhoods: [], streets: [] })

      if (!sessionUuid || cleanCep.length !== 8) {
        return { skipped: true }
      }

      commit('setAddressLookupLoading', { key: 'isCepLoading', value: true })

      try {
        const { data } = await axios.get(`/availability/${sessionUuid}/neighborhoods/cep/${cleanCep}`)

        commit('setAddressLookupLists', {
          neighborhoods: data ?? [],
          streets: []
        })

        if (!data?.length) {
          commit('setAddressLookupError', 'CEP não encontrado na nossa base, ou não pertence à cidade selecionada')
        }

        return { skipped: false, neighborhoods: data ?? [] }
      } catch (e) {
        commit('setAddressLookupError', 'Não foi possível consultar o CEP agora')
        return { skipped: false, error: true }
      } finally {
        commit('setAddressLookupLoading', { key: 'isCepLoading', value: false })
      }
    },

    async fetchStreetsByNeighborhood({ commit, state }, { neighborhood, cep }) {
      const sessionUuid = getSessionUuid(state)
      const cleanCep = onlyDigits(cep)

      commit('setAddressLookupLists', {
        neighborhoods: state.addressLookup.neighborhoods,
        streets: []
      })

      if (!sessionUuid || !neighborhood || cleanCep.length !== 8) {
        return { skipped: true }
      }

      commit('setAddressLookupLoading', { key: 'isAddressLoading', value: true })

      try {
        const { data } = await axios.get(`/availability/${sessionUuid}/neighborhoods/${neighborhood}/streets/cep/${cleanCep}`)

        commit('setAddressLookupLists', {
          neighborhoods: state.addressLookup.neighborhoods,
          streets: data ?? []
        })

        return { skipped: false, streets: data ?? [] }
      } catch (e) {
        commit('setAddressLookupError', 'Não foi possível consultar os endereços agora')
        return { skipped: false, error: true }
      } finally {
        commit('setAddressLookupLoading', { key: 'isAddressLoading', value: false })
      }
    },

    async submitAvailability({ state }) {
      const sessionUuid = getSessionUuid(state)
      if (!sessionUuid) return { skipped: true }

      const { availability } = state.form

      try {
        const { data } = await axios.post(`/availability/${sessionUuid}/check`, {
          name: availability.name,
          email: availability.email,
          phone: availability.phone,
          address_id: availability.address_id,
          cep: onlyDigits(availability.cep),
          neighborhood_id: availability.neighborhood_id,
          address_name: availability.address,
          neighborhood_name: availability.neighborhood,
          address_number: availability.address_number,
          address_complement: availability.address_complement || null,
        })
        return { success: true, data }
      } catch (e) {
        return { success: false, error: e }
      }
    },

    async validateContractWithBackend({ commit, state }) {
      const sessionUuid = getSessionUuid(state)

      commit('setBackendErrors', { section: 'contract', errors: {} })

      if (!sessionUuid) return { skipped: true, success: true }

      try {
        const { availability, contract } = state.form
        const response = await axios.post(`/contract/${sessionUuid}`, {
          name: availability.name,
          email: availability.email,
          phone: availability.phone,
          cpf: contract.cpf,
          rg: contract.rg,
          mother_name: contract.mother_name,
          birth_date: contract.birth_date,
          terms: contract.terms
        })

        if (response.data?.errors) {
          commit('setBackendErrors', {
            section: 'contract',
            errors: Object.entries(response.data.errors).reduce((mapped, [key, value]) => {
              mapped[key] = Array.isArray(value) ? value[0] : value
              return mapped
            }, {})
          })
        }

        return response.data ?? { success: true }
      } catch (e) {
        const errors = e.response?.data?.errors ?? {}

        commit('setBackendErrors', {
          section: 'contract',
          errors: Object.entries(errors).reduce((mapped, [key, value]) => {
            mapped[key] = Array.isArray(value) ? value[0] : value
            return mapped
          }, {})
        })

        return e.response?.data ?? { success: false }
      }
    },

    updateDocumentName({ commit, state }, payload) {
      commit('updateDocumentName', payload)
      persist(state)
    },

    setActiveStep({ commit, state }, stepKey) {
      commit('setActiveStep', stepKey)
      persist(state)
    },

    completeStep({ commit, state }, stepKey) {
      commit('completeStep', stepKey)
      persist(state)
    },

    async fetchChipService({ state, commit }) {
      const sessionUuid = getSessionUuid(state)
      if (!sessionUuid) return

      commit('setChipServiceLoading', true)
      commit('setChipServiceError', false)
      try {
        const { data } = await axios.get(`/internet-5g/${sessionUuid}/chip-service`)
        commit('setChipService', data)
      } catch (e) {
        console.error('[5G] fetchChipService error', e)
        commit('setChipServiceError', true)
      } finally {
        commit('setChipServiceLoading', false)
      }
    },

    resetPix({ commit }) {
      commit('setPixRequestInFlight', false)
      commit('setPixResult', null)
    },

    async checkInvoicePayment({ state }) {
      const sessionUuid = getSessionUuid(state)
      if (!sessionUuid) return { paid: false }

      try {
        const { data } = await axios.get(`/internet-5g/${sessionUuid}/invoice-status`)
        return data
      } catch (e) {
        console.error('[5G] checkInvoicePayment error', e)
        return { paid: false }
      }
    },

    async generatePix({ state, commit }) {
      if (state.pixRequest.inFlight) return state.pixRequest.result
      const sessionUuid = getSessionUuid(state)
      if (!sessionUuid) throw new Error('Sessão não encontrada')

      commit('setPixRequestInFlight', true)
      try {
        const { data } = await axios.post(`/internet-5g/${sessionUuid}/pix`)
        commit('setPixResult', data)
        return data
      } finally {
        commit('setPixRequestInFlight', false)
      }
    },

    async submitRevision({ state, commit }) {
      if (state.confirmRequest.inFlight) return { success: false, error: { title: 'Aguarde', content: 'Requisição em andamento' } }
      const sessionUuid = getSessionUuid(state)
      if (!sessionUuid) return { success: false, error: { title: 'Sessão expirada', content: 'Sessão não encontrada' } }

      commit('setConfirmRequestInFlight', true)
      commit('setConfirmRequestError', null)
      try {
        await axios.post(`/internet-5g/${sessionUuid}/confirm`)
        return { success: true }
      } catch (e) {
        const error = {
          title: e.response?.data?.message?.title ?? 'Erro ao confirmar contratação',
          content: e.response?.data?.message?.content ?? 'Não foi possível confirmar a contratação, tente novamente mais tarde.',
        }
        commit('setConfirmRequestError', error)
        return { success: false, error }
      } finally {
        commit('setConfirmRequestInFlight', false)
      }
    },

    reset({ commit, state }) {
      commit('reset')
      persist(state)
    },

    clearSession({ commit, dispatch }) {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem('session_uuid')
      localStorage.removeItem('selected_plan_id')
      dispatch('contractVerification/clearContractStatusFromStorage', null, { root: true })
      commit('reset')
    }
  },

  mutations: {
    hydrateDraft(state, draft) {
      state.campaignParams = draft.campaignParams ?? {}
      state.form = {
        ...initialForm(),
        ...(draft.form ?? {}),
        availability: {
          ...initialForm().availability,
          ...(draft.form?.availability ?? {})
        },
        contract: {
          ...initialForm().contract,
          ...(draft.form?.contract ?? {})
        },
        documents: {
          ...initialForm().documents,
          ...(draft.form?.documents ?? {})
        }
      }
      state.activeStep = stepOrder.includes(draft.activeStep)
        ? draft.activeStep
        : 'availability'
      state.completed = {
        ...initialCompleted(),
        ...(draft.completed ?? {})
      }
      state.backendErrors = {
        availability: {},
        contract: {}
      }
      state.addressLookup = {
        isCepLoading: false,
        isAddressLoading: false,
        cepError: '',
        neighborhoods: [],
        streets: []
      }
    },

    setCampaignParams(state, query) {
      state.campaignParams = Object.entries(query ?? {}).reduce((params, [key, value]) => {
        params[key] = Array.isArray(value) ? value.join(',') : String(value ?? '')
        return params
      }, {})
    },

    updateFormSection(state, { section, data }) {
      state.form[section] = {
        ...state.form[section],
        ...data
      }
      state.backendErrors[section] = {}
    },

    setBackendErrors(state, { section, errors }) {
      state.backendErrors[section] = errors
    },

    setAddressLookupLoading(state, { key, value }) {
      state.addressLookup[key] = value
    },

    setAddressLookupError(state, error) {
      state.addressLookup.cepError = error
    },

    setAddressLookupLists(state, { neighborhoods, streets }) {
      state.addressLookup.neighborhoods = neighborhoods
      state.addressLookup.streets = streets
    },

    updateDocumentName(state, { key, name }) {
      state.form.documents[key] = name
    },

    setActiveStep(state, stepKey) {
      state.activeStep = stepKey
    },

    completeStep(state, stepKey) {
      state.completed[stepKey] = true
    },

    setChipServiceLoading(state, value) {
      state.chipService.loading = value
    },

    setChipServiceError(state, value) {
      state.chipService.error = value
    },

    setChipService(state, data) {
      state.chipService.name = data.name ?? null
      state.chipService.price = data.price ?? null
    },

    setPixRequestInFlight(state, value) {
      state.pixRequest.inFlight = value
    },

    setPixResult(state, data) {
      state.pixRequest.result = data
    },

    setPixEnabled(state, value) {
      state.pixEnabled = Boolean(value)
      state.pixEnabledReady = true
    },

    setConfirmRequestInFlight(state, value) {
      state.confirmRequest.inFlight = value
    },

    setConfirmRequestError(state, error) {
      state.confirmRequest.error = error
    },

    reset(state) {
      state.activeStep = 'availability'
      state.completed = initialCompleted()
      state.campaignParams = {}
      state.form = initialForm()
      state.backendErrors = {
        availability: {},
        contract: {}
      }
      state.addressLookup = {
        isCepLoading: false,
        isAddressLoading: false,
        cepError: '',
        neighborhoods: [],
        streets: []
      }
      state.pixRequest = { inFlight: false, result: null }
      state.chipService = { name: null, price: null, loading: false, error: false }
      state.pixEnabled = true
      state.pixEnabledReady = false
      state.confirmRequest = { inFlight: false, error: null }
    }
  }
}
